import useAppContext from 'flow/hooks/useAppContext';
import useVoicemail from 'flow/hooks/useVoicemail';
import { Country } from 'flow/types/Country';
import { ExtensionKey } from 'flow/types/EntityKeys';

import { TypedMap } from 'flow/types/TypedMap';
import { Map } from 'immutable';
import { useAppSelector } from 'flow/hooks/useRedux';
import { isRetailer } from 'flow/selectors/selectors';
import { Retailers } from 'flow/utils/retailer-utils';
import {
    FREEPRO_VOICEMAIL_INTERNATIONAL_NUMBER,
    FREEPRO_VOICEMAIL_LOCAL_NUMBER,
    FREEPRO_VOICEMAIL_SHORT_NUMBER,
} from 'flow/utils/constants/retailers/FreeProConstants';

interface Props {
    extensionKey: ExtensionKey;
}

const VoicemailNumberOptions = ({ extensionKey }: Props) => {
    const { i18next } = useAppContext();
    const { voicemailSettings } = useVoicemail(extensionKey);

    const isRetailerFreePro = useAppSelector((state) =>
        isRetailer(state, Retailers.FREEPRO),
    );

    const activeCountries = useAppSelector((state) =>
        state.customer.getIn([extensionKey, 'activeCountries']),
    );

    if (!voicemailSettings || !activeCountries) {
        return null;
    }

    const getVoicemailCountryName = (countryCode: string) => {
        return activeCountries
            .find(
                (country: TypedMap<Country>) =>
                    country.get('countrycode') === countryCode.toLowerCase(),
                null,
                Map(),
            )
            .get('name');
    };

    const shortNumber = isRetailerFreePro
        ? FREEPRO_VOICEMAIL_SHORT_NUMBER
        : voicemailSettings.getIn([
              'voicemailNumbers',
              'speedDialNumber',
              'number',
          ]);

    const { nationalFormat, number, countryCode } = voicemailSettings
        .getIn(['voicemailNumbers', 'number'])
        .toJS();

    return (
        <p
            className="text-gray-800"
            dangerouslySetInnerHTML={{
                __html: i18next
                    .t('settings.voicemail.numbers_to_call', {
                        shortNumber,
                        localNumber: isRetailerFreePro
                            ? FREEPRO_VOICEMAIL_LOCAL_NUMBER
                            : nationalFormat,
                        intlNumber: isRetailerFreePro
                            ? FREEPRO_VOICEMAIL_INTERNATIONAL_NUMBER
                            : number,
                        countryName: getVoicemailCountryName(countryCode),
                    })
                    .replaceAll(/(\+?\d+)/g, '<strong>$1</strong>'),
            }}
        />
    );
};

export default VoicemailNumberOptions;
